// AccountsPanel — income & expense book. Fee collections and refunds come
// in automatically from the fee ledger; everything else (salaries, bills,
// uniform sales, donations…) is entered here.
import { useEffect, useState } from 'react';
import { Plus, Trash2, Download, TrendingUp, TrendingDown, Wallet, Scale } from 'lucide-react';
import { getAccountEntries, getAccountSummary, addAccountEntry, deleteAccountEntry } from '../api/client';
import Button from '../ui/Button';
import { Card, CardHeader, StatTile } from '../ui/Card';
import { Field, Msg, errText, inputCls, theadRowCls, EmptyRow } from '../ui/Form';
import { inr, fmtDate, todayISO, downloadCsv, PAYMENT_MODES } from '../ui/format';

const CATEGORIES = {
  expense: ['Salaries', 'Electricity', 'Water', 'Rent', 'Maintenance', 'Stationery', 'Fuel', 'Vehicle repair', 'Events', 'Internet & phone', 'Other'],
  income: ['Admission fee', 'Uniform sale', 'Book sale', 'Donation', 'Event income', 'Other'],
};

export default function AccountsPanel() {
  const [from, setFrom] = useState(() => `${todayISO().slice(0, 7)}-01`);
  const [to,   setTo]   = useState(todayISO);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ kind: 'expense', category: 'Salaries', amount: '', entry_date: todayISO(), mode: 'Cash', party: '', note: '' });
  const [msg,  setMsg]  = useState('');

  useEffect(() => { load(); }, [from, to]);
  async function load() {
    try {
      const [s, e] = await Promise.all([getAccountSummary({ from, to }), getAccountEntries({ from, to })]);
      setSummary(s); setRows(e.rows);
    } catch (err) { setMsg(errText(err)); }
  }

  async function add(e) {
    e.preventDefault(); setMsg('');
    try {
      await addAccountEntry({ ...form, amount: Number(form.amount) });
      setMsg(`${form.kind === 'expense' ? 'Expense' : 'Income'} of ${inr(form.amount)} saved ✅`);
      setForm({ ...form, amount: '', party: '', note: '' }); load();
    } catch (err) { setMsg(errText(err)); }
  }
  async function remove(r) {
    if (!window.confirm(`Delete ${r.category} ${inr(r.amount)}?`)) return;
    try { await deleteAccountEntry(r.id); load(); } catch (err) { setMsg(errText(err)); }
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h2 className="font-display font-extrabold text-xl text-ink">Accounts — Income &amp; Expenses</h2>
        <div className="flex gap-2 items-center">
          <input type="date" className={inputCls} value={from} max={to} onChange={e => setFrom(e.target.value)} aria-label="From" />
          <span className="text-ink-soft">to</span>
          <input type="date" className={inputCls} value={to} min={from} onChange={e => setTo(e.target.value)} aria-label="To" />
        </div>
      </div>
      <Msg msg={msg} className="mb-3" />

      {summary && (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(190px,1fr))] gap-3.5 mb-5">
          <StatTile tone="success" icon={Wallet} label="Fee collection" value={inr(summary.fee_collection)} caption={`${summary.fee_payments} payments`} />
          <StatTile tone="transit" icon={TrendingUp} label="Other income" value={inr(summary.other_income)} />
          <StatTile tone="critical" icon={TrendingDown} label="Expenses" value={inr(summary.expenses)} caption={[summary.salaries ? `${inr(summary.salaries)} salaries` : null, summary.refunds ? `${inr(summary.refunds)} fee refunds` : null].filter(Boolean).join(' · ') || null} />
          <StatTile tone={summary.net >= 0 ? 'success' : 'critical'} icon={Scale} label="Net" value={inr(summary.net)} />
        </div>
      )}

      <div className="grid grid-cols-[minmax(280px,340px)_1fr] gap-5 items-start max-[1000px]:grid-cols-1">
        <div className="flex flex-col gap-4">
          <Card className="p-5">
            <form onSubmit={add} className="flex flex-col gap-3">
              <div className="flex gap-1.5 bg-paper p-1 rounded-m">
                {['expense', 'income'].map(k => (
                  <button type="button" key={k} onClick={() => setForm({ ...form, kind: k, category: CATEGORIES[k][0] })}
                    className={`flex-1 py-1.5 rounded-s text-[13px] font-bold ${form.kind === k ? (k === 'expense' ? 'bg-critical text-white' : 'bg-success text-white') : 'text-ink-soft'}`}>
                    {k === 'expense' ? 'Expense' : 'Income'}
                  </button>
                ))}
              </div>
              <Field label="Category">
                <input required list={`cats-${form.kind}`} className={inputCls} value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} />
                <datalist id={`cats-${form.kind}`}>{CATEGORIES[form.kind].map(c => <option key={c} value={c} />)}</datalist>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Amount (₹)"><input required type="number" min="1" step="any" className={inputCls} value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} /></Field>
                <Field label="Date"><input type="date" className={inputCls} value={form.entry_date} onChange={e => setForm({ ...form, entry_date: e.target.value })} /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Mode"><select className={inputCls} value={form.mode} onChange={e => setForm({ ...form, mode: e.target.value })}>{PAYMENT_MODES.map(m => <option key={m}>{m}</option>)}</select></Field>
                <Field label={form.kind === 'expense' ? 'Paid to' : 'Received from'}><input className={inputCls} value={form.party} onChange={e => setForm({ ...form, party: e.target.value })} /></Field>
              </div>
              <Field label="Note"><input className={inputCls} value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} /></Field>
              <Button type="submit" className="w-fit flex items-center gap-1.5"><Plus size={14} /> Add {form.kind}</Button>
            </form>
          </Card>
          {summary && (summary.expense_by_category.length > 0 || summary.income_by_category.length > 0) && (
            <Card>
              <CardHeader title="By category" />
              {[...summary.income_by_category.map(c => ({ ...c, kind: 'income' })), ...summary.expense_by_category.map(c => ({ ...c, kind: 'expense' }))].map(c => (
                <div key={`${c.kind}-${c.category}`} className="flex justify-between px-5 py-2 border-t border-line-soft first:border-t-0 text-sm">
                  <span>{c.category}</span>
                  <b className={`tabular-nums ${c.kind === 'income' ? 'text-success' : 'text-critical'}`}>{c.kind === 'income' ? '+' : '−'}{inr(c.amount)}</b>
                </div>
              ))}
            </Card>
          )}
        </div>

        <Card>
          <CardHeader title={`Entries (${rows.length})`} action={
            <Button variant="secondary" className="!py-1.5 !text-xs flex items-center gap-1.5" disabled={!rows.length}
              onClick={() => downloadCsv(`accounts_${from}_to_${to}.csv`, [
                { key: 'entry_date', label: 'Date' }, { key: 'kind', label: 'Type' }, { key: 'category', label: 'Category' },
                { key: 'party', label: 'Party' }, { key: 'mode', label: 'Mode' }, { key: 'note', label: 'Note' }, { key: 'amount', label: 'Amount' },
              ], rows)}><Download size={13} /> CSV</Button>
          } />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className={theadRowCls}><th className="px-4 py-2.5">Date</th><th className="px-4 py-2.5">Category</th><th className="px-4 py-2.5">Party / note</th><th className="px-4 py-2.5">Mode</th><th className="px-4 py-2.5 text-right">Amount</th><th className="px-4 py-2.5"></th></tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className="border-t border-line-soft">
                    <td className="px-4 py-3 whitespace-nowrap">{fmtDate(r.entry_date)}</td>
                    <td className="px-4 py-3 font-semibold">{r.category}</td>
                    <td className="px-4 py-3 text-ink-soft">{[r.party, r.note].filter(Boolean).join(' · ') || '—'}</td>
                    <td className="px-4 py-3">{r.mode || '—'}</td>
                    <td className={`px-4 py-3 text-right font-bold tabular-nums ${r.kind === 'income' ? 'text-success' : 'text-critical'}`}>{r.kind === 'income' ? '+' : '−'}{inr(r.amount)}</td>
                    <td className="px-4 py-3"><button onClick={() => remove(r)} aria-label="Delete entry" className="text-ink-soft hover:text-critical p-1"><Trash2 size={14} /></button></td>
                  </tr>
                ))}
                {!rows.length && <EmptyRow cols={6}>No entries in this period. Fee collections, refunds and payroll salaries are counted automatically in the tiles above.</EmptyRow>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
